/*
 * v1.15.0 — Workspace navigation tree, restructured into the 9 spec
 * modules from §3.2 of the navigation-restructuring brief:
 *
 *   1. Dashboard
 *   2. Data Sources & Sync
 *   3. Variables & Mappings
 *   4. Aggregation & Domains
 *   5. Indicators & Reports
 *   6. Analysis & Exploration
 *   7. Members & Permissions
 *   8. Settings
 *   9. Help & Support
 *
 * Plus a 10th group "Data capture" — TallyStream-local data entry that
 * doesn't map cleanly to any of the 9 (it's an optional companion
 * surface). Kept as its own module so it stays discoverable.
 *
 * Non-breaking refactor:
 *   - Every existing `to:` path is preserved verbatim.
 *   - Permission gates unchanged from the prior 9-group layout.
 *   - /app/* remains the canonical URL prefix for the workspace console.
 */

import type { NavTree } from "./navigationTypes.js";

export const workspaceNavigation: NavTree = {
  console: "workspace",
  basePath: "/app",
  groups: [
    // --- 1. Dashboard ---------------------------------------------------
    {
      id: "dashboard",
      label: "Dashboard",
      items: [
        {
          to: "/app",
          label: "Dashboard",
          description: "Workspace health: sync, ingestion, recent failures, quota, plan, onboarding.",
          status: "active",
          requirePermissions: ["workspace.dashboard.view"]
        }
      ]
    },

    // --- 2. Data Sources & Sync -----------------------------------------
    // Combines legacy "Data activity" + "Administration — DHIS2 integration"
    // since both are about the source-system pipeline.
    {
      id: "data-sources-sync",
      label: "Data Sources & Sync",
      items: [
        {
          to: "/app/administration/connections/instances",
          label: "DHIS2 instances",
          description: "Workspace DHIS2 service profiles; credentials; rotation.",
          status: "active",
          requirePermissions: ["workspace.dhis2_connections.view"]
        },
        {
          to: "/app/administration/connections/app-clients",
          label: "DHIS2 app clients",
          description: "Embedded app client IDs, authorities, secrets.",
          status: "active",
          requirePermissions: ["workspace.dhis2_connections.view"]
        },
        {
          to: "/app/administration/connections/forwarding",
          label: "Forwarding hub",
          description: "Forwarding destinations, schedules, recent runs.",
          status: "active",
          requirePermissions: ["workspace.forwarding.view"]
        },
        {
          to: "/app/admin/dhis2/metadata-browser",
          label: "Metadata browser",
          description: "Browse synced aggregate + tracker metadata for this workspace.",
          status: "active"
        },
        {
          to: "/app/admin/dhis2/secrets",
          label: "Integration secrets",
          description: "Workspace integration secrets (DHIS2 PATs, webhook tokens) with rotation status.",
          status: "active"
        },
        {
          to: "/app/live",
          label: "Live monitor",
          description: "Real-time workspace event feed (metadata, data, tally, indicator, jobs, DLQ).",
          status: "active"
        },
        {
          to: "/app/stream-console",
          label: "Stream console",
          description: "Per-instance ingestion status, accept/duplicate/reject counts.",
          status: "active"
        },
        {
          to: "/app/jobs",
          label: "Sync jobs",
          description: "Workspace job history with status, progress, retry.",
          status: "active"
        },
        {
          to: "/app/data-activity/synced-data",
          label: "Synced source data",
          description: "Raw records pulled from DHIS2: tracker events, tracked entities, aggregate facts.",
          status: "active"
        },
        {
          to: "/app/data-activity/transfer-logs",
          label: "Transfer logs",
          description: "Workspace ingestion + forwarding runs with retry / replay / DLQ.",
          status: "active"
        },
        {
          to: "/app/data-activity/watermarks",
          label: "Watermarks",
          description: "Sync watermarks per source with lag indicator and replay-from action.",
          status: "active"
        },
        {
          to: "/app/data-activity/failures",
          label: "Failures & dead-letter",
          description: "Failed jobs and dead-lettered envelopes with detail + recovery actions.",
          status: "active"
        }
      ]
    },

    // --- 3. Variables & Mappings ----------------------------------------
    {
      id: "variables-mappings",
      label: "Variables & Mappings",
      items: [
        {
          to: "/app/metadata/tally-variables",
          label: "TallyMetric variables",
          description: "Auto-generated catalogue derived from synced metadata — the variables this workspace can tally against.",
          status: "active"
        },
        {
          to: "/app/tallying/tallymetrics",
          label: "TallyMetrics (data)",
          description: "Tallied counts and totals — bucket data computed from tracker events against the variable catalogue.",
          status: "active"
        },
        {
          to: "/app/tallying/mappings",
          label: "Mapping console",
          description: "Tally → indicator, tracker → aggregate, aggregate → publication mappings.",
          status: "active"
        },
        {
          to: "/app/tallying/reconciliation",
          label: "Reconciliation",
          description: "Source vs platform reconciliation results and corrective actions.",
          status: "active"
        }
      ]
    },

    // --- 4. Aggregation & Domains ---------------------------------------
    {
      id: "aggregation-domains",
      label: "Aggregation & Domains",
      items: [
        {
          to: "/app/tallying/aggregate-domains",
          label: "Aggregate domains",
          description: "Domain studio: schemas, rollup rules, validation, ownership.",
          status: "active"
        },
        {
          to: "/app/tallying/rollups",
          label: "Rollups",
          description: "Rollup job status, rollup facts, manual rollup actions.",
          status: "active"
        }
      ]
    },

    // --- 5. Indicators & Reports ----------------------------------------
    {
      id: "indicators-reports",
      label: "Indicators & Reports",
      requireAnyPermission: ["workspace.public_portal.view", "workspace.analytics.view"],
      items: [
        {
          to: "/app/tallying/indicators",
          label: "Indicators",
          description: "Indicator definitions and computed values; preview output.",
          status: "active"
        },
        {
          to: "/app/portals",
          label: "Public portals",
          description: "Public dataset / indicator portals owned by this workspace.",
          status: "active",
          requirePermissions: ["workspace.public_portal.view"]
        }
      ]
    },

    // --- 6. Analysis & Exploration --------------------------------------
    {
      id: "analysis-exploration",
      label: "Analysis & Exploration",
      requireAnyPermission: ["workspace.datasets.view", "workspace.analytics.view"],
      items: [
        {
          to: "/app/datasets",
          label: "Datasets",
          description: "Dataset inventory with sample rows, sources, refresh status.",
          status: "active",
          requireAnyPermission: ["workspace.datasets.view", "workspace.analytics.view"]
        },
        {
          to: "/app/data-preview",
          label: "Warehouse rollup explorer",
          description: "Pre-aggregated rows from the workspace warehouse, grouped by metric / period / org unit.",
          status: "active",
          requireAnyPermission: ["workspace.datasets.view", "workspace.analytics.view"]
        },
        {
          to: "/app/insights/analytics",
          label: "Analytics explorer",
          description: "Pivot / chart / table analytics with filters and export.",
          status: "active",
          requireAnyPermission: ["workspace.analytics.view"]
        },
        {
          to: "/app/insights/warehouse",
          label: "Warehouse explorer",
          description: "Read-only workspace warehouse queries and prebuilt dashboards.",
          status: "active",
          requireAnyPermission: ["workspace.analytics.view"]
        },
        {
          to: "/app/insights/lineage",
          label: "Lineage explorer",
          description: "Source → contribution → bucket → indicator → publication trail.",
          status: "active",
          requireAnyPermission: ["workspace.analytics.view"]
        }
      ]
    },

    // --- 7. Members & Permissions ---------------------------------------
    {
      id: "members-permissions",
      label: "Members & Permissions",
      requireAnyPermission: [
        "workspace.members.view",
        "workspace.billing.view",
        "workspace.subscription.manage"
      ],
      items: [
        {
          to: "/app/administration/workspace/team",
          label: "Members & roles",
          description: "Invite members, assign workspace roles.",
          status: "active",
          requirePermissions: ["workspace.members.view"]
        },
        {
          to: "/app/administration/workspace/subscriptions",
          label: "Subscription",
          description: "View plan, upgrade / downgrade, change plan.",
          status: "active",
          requireAnyPermission: ["workspace.billing.view", "workspace.subscription.manage"]
        },
        {
          to: "/app/administration/workspace/billing",
          label: "Billing & invoices",
          description: "Invoice history, payments, payment methods.",
          status: "active",
          requireAnyPermission: ["workspace.billing.view", "workspace.billing.manage"]
        },
        {
          to: "/app/admin/workspace/usage",
          label: "Usage & quotas",
          description: "Usage vs plan quotas: seats, instances, ingestion, storage.",
          status: "active"
        }
      ]
    },

    // --- 8. Settings ----------------------------------------------------
    {
      id: "settings",
      label: "Settings",
      requireAnyPermission: ["workspace.settings.view", "workspace.security.view"],
      items: [
        {
          to: "/app/administration/settings/general",
          label: "General settings",
          description: "Default org unit, active DHIS2 instance, startup module.",
          status: "active",
          requireAnyPermission: ["workspace.settings.view", "workspace.settings.manage"]
        },
        {
          to: "/app/administration/settings/security",
          label: "Security settings",
          description: "MFA status, session policy, secrets-rotation reminders.",
          status: "active",
          requireAnyPermission: ["workspace.security.view", "workspace.security.manage"]
        },
        {
          to: "/app/admin/settings/pipeline",
          label: "Pipeline settings",
          description: "Rollup strictness, ingestion strictness, sync schedule preferences.",
          status: "active"
        },
        {
          to: "/app/admin/settings/notifications",
          label: "Notification settings",
          description: "Workspace notification preferences (alerts, digests, webhooks).",
          status: "active"
        }
      ]
    },

    // --- 9. Help & Support ----------------------------------------------
    {
      id: "help-support",
      label: "Help & Support",
      items: [
        {
          to: "/app/audit",
          label: "Audit log",
          description: "Workspace audit events filtered by category / severity / actor / date.",
          status: "active",
          requirePermissions: ["workspace.audit.view"]
        },
        {
          to: "/app/support",
          label: "Support",
          description: "Workspace support entry; ticket history.",
          status: "active",
          requireAnyPermission: ["workspace.support.view", "workspace.support.create"]
        },
        {
          to: "/app/help/docs",
          label: "Documentation",
          description: "Quick-start guides, help articles, embedded docs.",
          status: "active"
        },
        {
          to: "/app/help/onboarding",
          label: "Onboarding checklist",
          description: "Step-by-step setup: connect DHIS2, sync, define domain, run ingestion.",
          status: "active"
        }
      ]
    },

    // --- 10. Data capture (workspace-local data entry) ------------------
    // Optional companion surface; not in the spec's 9-module list but
    // discoverable here so legacy users keep finding it.
    {
      id: "data-capture",
      label: "Data capture",
      items: [
        { to: "/app/capture", label: "Capture dashboard", description: "TallyStream-local data capture overview.", status: "active" },
        { to: "/app/capture/forms", label: "Forms", description: "Metadata-driven local forms.", status: "active" },
        { to: "/app/capture/forms/new", label: "Form builder", description: "Build / edit a capture form.", status: "active" },
        { to: "/app/capture/drafts", label: "Drafts", description: "Saved-but-unsubmitted captures.", status: "active" },
        { to: "/app/capture/submissions", label: "Submissions", description: "Submitted captures with processing status.", status: "active" },
        { to: "/app/capture/review", label: "Review queue", description: "Submissions awaiting approval.", status: "active" },
        { to: "/app/capture/analytics", label: "Capture analytics", description: "Capture quality metrics.", status: "active" },
        { to: "/app/capture/settings", label: "Capture settings", description: "Capture-module configuration.", status: "active" }
      ]
    }
  ]
};
